import { observer } from 'mobx-react-lite'
import { type FC, type UIEvent, useCallback, useEffect, useRef } from 'react'
import {
  EnumSpinnerLoaderPlacement,
  type IScrollbarsPropsRef,
  Scrollbar,
  SpinnerLoader,
  Typography,
} from 'shared/ui'
import { useEntitiesContext } from 'entities/EntitiesExport'
import type { IResultsAllIntegrations } from 'pages/conversations/pages/search/ui/Results/store/types'
import { ContactCard } from 'pages/conversations/pages/search/ui/Results/ui/ContactCard/ContactCard'
import { useResultsContext } from 'pages/conversations/pages/search/ui/Results/context/resultsContext'
import { logV2SearchEvent } from 'pages/conversations/pages/search/utils/logSearchEvent'
import styles from './styles.module.scss'

type ISectionAllContactsListProps = {
  activeIntegration: IResultsAllIntegrations
}

export const SectionAllContactsList: FC<ISectionAllContactsListProps> = observer(
  ({ activeIntegration }) => {
    const entities = useEntitiesContext()
    const { handleGoToProfile } = useResultsContext()
    const store = activeIntegration.store
    const key = activeIntegration.key
    const items = store.items
    const scrollRef = useRef<IScrollbarsPropsRef | null>(null)

    const handleScroll = useCallback(
      (e: UIEvent) => {
        const target = e.target as HTMLDivElement
        const scrollHeight = target.scrollHeight
        const scrollTop = target.scrollTop
        const offsetHeight = target.offsetHeight
        const indentBottom = 20

        if (store.loading) return
        if (store.loadingMore) return
        if (!store.hasMore) return
        if (scrollHeight <= scrollTop + offsetHeight + indentBottom) {
          store.fetch({ more: true })
        }
      },
      [key]
    )

    useEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollToTop()
      }
    }, [key])

    if (store.loading) {
      return (
        <div className={styles.loading}>
          <SpinnerLoader
            ariaLabel={'SectionAllContactsList'}
            size={20}
            placement={EnumSpinnerLoaderPlacement.Top}
          />
        </div>
      )
    }

    if (!items.length) {
      return (
        <Typography
          tag={'div'}
          variant={'body-sm-regular'}
          className={styles.empty}
          ariaLabel={'empty'}
        >
          No contacts found
        </Typography>
      )
    }

    return (
      <Scrollbar ref={scrollRef} indentX={20} onScroll={handleScroll}>
        {items.map((item) => {
          return (
            <ContactCard
              key={item.key}
              item={item}
              onActionClick={(item) => {
                handleGoToProfile(item)

                logV2SearchEvent(
                  'Search Page Result Clicked',
                  {
                    event_id: 'search_page_result_clicked',
                    SearchResultClickedType: 'Contact_separate_tab',
                  },
                  entities
                )
              }}
              isHideIconIntegration={true}
            />
          )
        })}
        {store.loadingMore && (
          <div className={styles.loadingMore}>
            <SpinnerLoader ariaLabel={'SectionAllContactsList'} size={20} />
          </div>
        )}
      </Scrollbar>
    )
  }
)
